// SPDX-License-Identifier: AGPL-3.0-or-later

// Curated Phosphor icons offered as channel glyphs. Keys are stored in channel data, so never
// rename or remove one; add new icons with new keys instead.

import {
	AirplaneIcon,
	AlienIcon,
	ArchiveIcon,
	ArticleIcon,
	AtIcon,
	AtomIcon,
	BankIcon,
	BarbellIcon,
	BasketballIcon,
	BeerSteinIcon,
	BellIcon,
	BellRingingIcon,
	BicycleIcon,
	BirdIcon,
	BookmarkIcon,
	BookOpenIcon,
	BooksIcon,
	BrainIcon,
	BriefcaseIcon,
	BroadcastIcon,
	BugIcon,
	ButterflyIcon,
	CakeIcon,
	CalculatorIcon,
	CalendarDotsIcon,
	CameraIcon,
	CardsIcon,
	CarIcon,
	CatIcon,
	ChartLineIcon,
	ChatCenteredDotsIcon,
	ChatCircleIcon,
	ChatsCircleIcon,
	ChatTextIcon,
	ClipboardTextIcon,
	ClockIcon,
	CloudIcon,
	CodeIcon,
	CoffeeIcon,
	ConfettiIcon,
	CookieIcon,
	CpuIcon,
	CrosshairIcon,
	CrownIcon,
	CurrencyDollarIcon,
	DatabaseIcon,
	DesktopIcon,
	DetectiveIcon,
	DeviceMobileIcon,
	DiamondIcon,
	DiceFiveIcon,
	DogIcon,
	DropIcon,
	EnvelopeIcon,
	FilmSlateIcon,
	FilmStripIcon,
	FireIcon,
	FishIcon,
	FlagIcon,
	FlaskIcon,
	FlowerIcon,
	FootballIcon,
	GameControllerIcon,
	GavelIcon,
	GearIcon,
	GhostIcon,
	GiftIcon,
	GitBranchIcon,
	GlobeIcon,
	GraduationCapIcon,
	GuitarIcon,
	HamburgerIcon,
	HammerIcon,
	HandshakeIcon,
	HandWavingIcon,
	HeadphonesIcon,
	HeartIcon,
	type Icon,
	IceCreamIcon,
	ImageIcon,
	InfoIcon,
	JoystickIcon,
	KeyIcon,
	LeafIcon,
	LifebuoyIcon,
	LightbulbIcon,
	LightningIcon,
	LinkIcon,
	ListChecksIcon,
	LockIcon,
	MapPinIcon,
	MedalIcon,
	MegaphoneIcon,
	MegaphoneSimpleIcon,
	MicrophoneIcon,
	MoonIcon,
	MountainsIcon,
	MusicNotesIcon,
	NewspaperIcon,
	NotepadIcon,
	NoteIcon,
	PaintBrushIcon,
	PaletteIcon,
	PawPrintIcon,
	PencilSimpleIcon,
	PizzaIcon,
	PlanetIcon,
	PopcornIcon,
	PushPinIcon,
	PuzzlePieceIcon,
	QuestionIcon,
	RadioIcon,
	RobotIcon,
	RocketIcon,
	ScrollIcon,
	ShieldCheckIcon,
	ShieldIcon,
	ShoppingCartIcon,
	SkullIcon,
	SmileyIcon,
	SnowflakeIcon,
	SoccerBallIcon,
	SparkleIcon,
	StarIcon,
	StickerIcon,
	StorefrontIcon,
	StrategyIcon,
	SunIcon,
	SwordIcon,
	TagIcon,
	TargetIcon,
	TelevisionIcon,
	TerminalIcon,
	TicketIcon,
	TranslateIcon,
	TreeIcon,
	TrophyIcon,
	UserCircleIcon,
	UsersIcon,
	UsersThreeIcon,
	VideoCameraIcon,
	WarningIcon,
	WifiHighIcon,
	WineIcon,
	WrenchIcon,
} from '@phosphor-icons/react';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export interface ChannelGlyphIconEntry {
	key: string;
	icon: Icon;
	/** Extra search words (English + Turkish) besides the key itself. */
	tags: string;
}

export interface ChannelGlyphIconGroup {
	id: string;
	label: MessageDescriptor;
	icons: ReadonlyArray<ChannelGlyphIconEntry>;
}

const e = (key: string, icon: Icon, tags = ''): ChannelGlyphIconEntry => ({key, icon, tags});

export const CHANNEL_GLYPH_ICON_GROUPS: ReadonlyArray<ChannelGlyphIconGroup> = [
	{
		id: 'talk',
		label: msg({message: 'Talk', comment: 'Channel icon picker category: chat and announcement icons.'}),
		icons: [
			e('chat', ChatCircleIcon, 'sohbet mesaj'),
			e('chats', ChatsCircleIcon, 'sohbet genel'),
			e('chat-text', ChatTextIcon, 'yazı metin'),
			e('chat-dots', ChatCenteredDotsIcon, 'yazıyor'),
			e('megaphone', MegaphoneIcon, 'duyuru announcement'),
			e('megaphone-simple', MegaphoneSimpleIcon, 'duyuru'),
			e('bell', BellIcon, 'bildirim notification'),
			e('bell-ringing', BellRingingIcon, 'bildirim'),
			e('info', InfoIcon, 'bilgi'),
			e('question', QuestionIcon, 'soru yardım help'),
			e('warning', WarningIcon, 'uyarı'),
			e('pin', PushPinIcon, 'sabit'),
			e('link', LinkIcon, 'bağlantı'),
			e('globe', GlobeIcon, 'dünya web'),
			e('at', AtIcon, 'etiket mention'),
			e('envelope', EnvelopeIcon, 'posta mail'),
			e('newspaper', NewspaperIcon, 'haber news'),
			e('broadcast', BroadcastIcon, 'yayın canlı live'),
		],
	},
	{
		id: 'community',
		label: msg({message: 'Community', comment: 'Channel icon picker category: people and server life icons.'}),
		icons: [
			e('wave', HandWavingIcon, 'merhaba hoşgeldin welcome'),
			e('handshake', HandshakeIcon, 'ortaklık partner'),
			e('users', UsersIcon, 'üyeler members'),
			e('users-three', UsersThreeIcon, 'topluluk grup'),
			e('user', UserCircleIcon, 'profil'),
			e('rules', ScrollIcon, 'kurallar rules'),
			e('clipboard', ClipboardTextIcon, 'liste'),
			e('checklist', ListChecksIcon, 'görev rol'),
			e('notepad', NotepadIcon, 'not'),
			e('note', NoteIcon, 'not'),
			e('shield-check', ShieldCheckIcon, 'doğrulama yetkili mod'),
			e('gavel', GavelIcon, 'ceza moderasyon'),
			e('lock', LockIcon, 'özel gizli'),
			e('key', KeyIcon, 'anahtar'),
			e('crown', CrownIcon, 'vip kral'),
			e('gift', GiftIcon, 'çekiliş hediye giveaway'),
			e('confetti', ConfettiIcon, 'etkinlik kutlama'),
			e('calendar', CalendarDotsIcon, 'takvim etkinlik event'),
			e('ticket', TicketIcon, 'destek bilet'),
			e('lifebuoy', LifebuoyIcon, 'destek yardım support'),
			e('star', StarIcon, 'yıldız öne çıkan'),
			e('heart', HeartIcon, 'kalp sevgi'),
			e('sparkle', SparkleIcon, 'parıltı yeni'),
			e('smiley', SmileyIcon, 'eğlence'),
		],
	},
	{
		id: 'games',
		label: msg({message: 'Games', comment: 'Channel icon picker category: gaming icons.'}),
		icons: [
			e('controller', GameControllerIcon, 'oyun game'),
			e('joystick', JoystickIcon, 'oyun'),
			e('sword', SwordIcon, 'kılıç rpg'),
			e('shield', ShieldIcon, 'kalkan'),
			e('crosshair', CrosshairIcon, 'fps nişan'),
			e('trophy', TrophyIcon, 'turnuva kupa'),
			e('medal', MedalIcon, 'madalya'),
			e('target', TargetIcon, 'hedef'),
			e('puzzle', PuzzlePieceIcon, 'bulmaca'),
			e('dice', DiceFiveIcon, 'zar'),
			e('cards', CardsIcon, 'kart'),
			e('strategy', StrategyIcon, 'strateji taktik'),
			e('ghost', GhostIcon, 'hayalet korku'),
			e('skull', SkullIcon, 'kurukafa'),
			e('alien', AlienIcon, 'uzaylı'),
			e('robot', RobotIcon, 'bot'),
			e('detective', DetectiveIcon, 'dedektif'),
			e('diamond', DiamondIcon, 'elmas'),
			e('fire', FireIcon, 'ateş popüler'),
			e('lightning', LightningIcon, 'şimşek hızlı'),
			e('rocket', RocketIcon, 'roket'),
			e('flag', FlagIcon, 'bayrak'),
		],
	},
	{
		id: 'media',
		label: msg({message: 'Media', comment: 'Channel icon picker category: music, video and art icons.'}),
		icons: [
			e('music', MusicNotesIcon, 'müzik'),
			e('headphones', HeadphonesIcon, 'kulaklık müzik'),
			e('microphone', MicrophoneIcon, 'mikrofon podcast'),
			e('guitar', GuitarIcon, 'gitar'),
			e('radio', RadioIcon, 'radyo'),
			e('video', VideoCameraIcon, 'video'),
			e('film', FilmStripIcon, 'film'),
			e('clapper', FilmSlateIcon, 'sinema'),
			e('popcorn', PopcornIcon, 'film izle'),
			e('tv', TelevisionIcon, 'dizi'),
			e('camera', CameraIcon, 'fotoğraf'),
			e('image', ImageIcon, 'resim görsel'),
			e('palette', PaletteIcon, 'sanat'),
			e('brush', PaintBrushIcon, 'çizim'),
			e('pencil', PencilSimpleIcon, 'yazı'),
			e('sticker', StickerIcon, 'çıkartma'),
		],
	},
	{
		id: 'tech',
		label: msg({message: 'Tech & learning', comment: 'Channel icon picker category: technology and education icons.'}),
		icons: [
			e('code', CodeIcon, 'kod yazılım'),
			e('terminal', TerminalIcon, 'konsol'),
			e('bug', BugIcon, 'hata'),
			e('cpu', CpuIcon, 'donanım'),
			e('desktop', DesktopIcon, 'bilgisayar pc'),
			e('mobile', DeviceMobileIcon, 'telefon'),
			e('database', DatabaseIcon, 'veri'),
			e('git', GitBranchIcon, 'github'),
			e('wifi', WifiHighIcon, 'internet'),
			e('gear', GearIcon, 'ayar'),
			e('wrench', WrenchIcon, 'tamir'),
			e('hammer', HammerIcon, 'yapım'),
			e('atom', AtomIcon, 'bilim'),
			e('flask', FlaskIcon, 'deney'),
			e('calculator', CalculatorIcon, 'matematik'),
			e('graduation', GraduationCapIcon, 'okul eğitim'),
			e('book', BookOpenIcon, 'kitap'),
			e('books', BooksIcon, 'kütüphane'),
			e('article', ArticleIcon, 'makale blog'),
			e('lightbulb', LightbulbIcon, 'fikir öneri'),
			e('brain', BrainIcon, 'beyin'),
			e('translate', TranslateIcon, 'dil çeviri'),
			e('chart', ChartLineIcon, 'grafik istatistik'),
			e('archive', ArchiveIcon, 'arşiv'),
		],
	},
	{
		id: 'life',
		label: msg({message: 'Life & nature', comment: 'Channel icon picker category: food, travel, sport and nature icons.'}),
		icons: [
			e('coffee', CoffeeIcon, 'kahve sohbet'),
			e('pizza', PizzaIcon, 'yemek'),
			e('burger', HamburgerIcon, 'yemek'),
			e('cake', CakeIcon, 'doğum günü'),
			e('ice-cream', IceCreamIcon, 'dondurma'),
			e('cookie', CookieIcon, 'kurabiye'),
			e('wine', WineIcon, 'içecek'),
			e('beer', BeerSteinIcon, 'içecek'),
			e('soccer', SoccerBallIcon, 'futbol spor'),
			e('basketball', BasketballIcon, 'basketbol'),
			e('football', FootballIcon, 'amerikan futbolu'),
			e('barbell', BarbellIcon, 'spor fitness'),
			e('bicycle', BicycleIcon, 'bisiklet'),
			e('car', CarIcon, 'araba'),
			e('airplane', AirplaneIcon, 'seyahat uçak'),
			e('map-pin', MapPinIcon, 'konum'),
			e('cat', CatIcon, 'kedi'),
			e('dog', DogIcon, 'köpek'),
			e('paw', PawPrintIcon, 'hayvan'),
			e('bird', BirdIcon, 'kuş'),
			e('fish', FishIcon, 'balık'),
			e('butterfly', ButterflyIcon, 'kelebek'),
			e('leaf', LeafIcon, 'yaprak'),
			e('tree', TreeIcon, 'ağaç'),
			e('flower', FlowerIcon, 'çiçek'),
			e('mountains', MountainsIcon, 'dağ'),
			e('sun', SunIcon, 'güneş'),
			e('moon', MoonIcon, 'ay gece'),
			e('cloud', CloudIcon, 'bulut'),
			e('snowflake', SnowflakeIcon, 'kar kış'),
			e('drop', DropIcon, 'su'),
			e('planet', PlanetIcon, 'gezegen uzay'),
		],
	},
	{
		id: 'work',
		label: msg({message: 'Work & shop', comment: 'Channel icon picker category: business and shopping icons.'}),
		icons: [
			e('briefcase', BriefcaseIcon, 'iş'),
			e('bank', BankIcon, 'banka'),
			e('dollar', CurrencyDollarIcon, 'para'),
			e('cart', ShoppingCartIcon, 'alışveriş'),
			e('store', StorefrontIcon, 'mağaza market'),
			e('tag', TagIcon, 'etiket fiyat'),
			e('bookmark', BookmarkIcon, 'kaydet'),
			e('clock', ClockIcon, 'saat zaman'),
		],
	},
];

const ICONS_BY_KEY = new Map<string, Icon>(
	CHANNEL_GLYPH_ICON_GROUPS.flatMap((group) => group.icons.map((entry) => [entry.key, entry.icon] as const)),
);

export function getChannelGlyphIcon(key: string): Icon | null {
	return ICONS_BY_KEY.get(key) ?? null;
}

/** Tints offered in the picker; any other hex can still be chosen with the custom colour input. */
export const CHANNEL_GLYPH_COLORS: ReadonlyArray<string> = [
	'9b8cff',
	'5b8def',
	'2fb5d2',
	'2ecc8f',
	'8bcf3f',
	'f5c84c',
	'ff9f43',
	'ff6b6b',
	'f368b5',
	'c77dff',
	'a3a9b7',
	'f2f3f5',
];

export const CHANNEL_GLYPH_DEFAULT_COLOR = CHANNEL_GLYPH_COLORS[0];
